/**
 * Group system v2 — one string lookup for the `dashboard.groupV2.*` namespace
 * that works OUTSIDE React and for an EXPLICIT locale.
 *
 * Why not just `useDashboardTranslation`: the foundations render EN and AR side
 * by side (showcase, PDF-style previews, the agency share page later), and the
 * pure view functions (`groupStatusView`, `moneyBadgeView`, …) must produce the
 * exact sentence a test can pin. Both need a locale that is a parameter, not
 * the signed-in user's preference. The JSON files stay the single source of
 * truth — this reads the same `src/locales/{en,ar}.json` the context reads.
 *
 * ⛔ Arabic is written natively in the Saudi hotel register (design brief §4)
 *    and keeps LATIN digits (brand rule). Never machine-translate a key here.
 */

import en from '@/locales/en.json'
import ar from '@/locales/ar.json'
import { interpolate } from '@/lib/i18n/interpolate'

/** The two locales the group screens ship in. */
export type GroupLocale = 'en' | 'ar'

type Tree = Record<string, unknown>

const ROOTS: Record<GroupLocale, Tree> = {
  en: ((en as Tree).dashboard as Tree).groupV2 as Tree,
  ar: ((ar as Tree).dashboard as Tree).groupV2 as Tree,
}

function lookup(tree: Tree | undefined, path: string): unknown {
  return path.split('.').reduce<unknown>((node, part) => {
    if (node && typeof node === 'object' && part in (node as Tree)) return (node as Tree)[part]
    return undefined
  }, tree)
}

/**
 * Translate a `dashboard.groupV2.<key>` string for `locale`, filling `{param}`
 * placeholders. Falls back to English, then to the key itself (so a missing
 * string is visible in review instead of rendering blank).
 *
 * @param locale - 'en' | 'ar'.
 * @param key - Path below `dashboard.groupV2`, e.g. `status.option`.
 * @param params - Placeholder values; numbers are written with Latin digits.
 */
export function groupT(locale: GroupLocale, key: string, params?: Record<string, string | number>): string {
  const raw = lookup(ROOTS[locale], key) ?? lookup(ROOTS.en, key)
  if (typeof raw !== 'string') return key
  return params ? interpolate(withCounts(locale, withArabicCase(raw, params), params), params) : raw
}

/**
 * Translate any `dashboard.<key>` string for `locale` - for a sentence that
 * lives outside `groupV2` (e.g. `groupBlocks.gapFill.rowErrors.*`, shared with
 * the guest forms). Same fallbacks as {@link groupT}.
 */
export function dashboardT(locale: GroupLocale, key: string, params?: Record<string, string | number>): string {
  const root = (l: GroupLocale) => ((l === 'ar' ? ar : en) as Tree).dashboard as Tree
  const raw = lookup(root(locale), key) ?? lookup(root('en'), key)
  if (typeof raw !== 'string') return key
  return params ? interpolate(raw, params) : raw
}

/**
 * A count WITH its noun, written by the template: `{count|rooms}` becomes
 * `groupCount(locale, 'rooms', count)` - "1 room" / "3 rooms", «غرفة واحدة» /
 * «غرفتان» / «3 غرف» / «15 غرفة». Fixing a wrong plural ("2 charge(s)",
 * «بين 2 زوج», «{count} غرفة» for every count) is then a locale edit: the
 * call site keeps passing the number it always passed.
 *
 * A value that is not a number (a caller that already passes a phrase) is
 * written as it is; an unknown placeholder is left for `interpolate`.
 * Shared with the dashboard's `t()` (contexts/LanguageContext) so a key outside
 * `groupV2` can use the same units.
 */
export function withCounts(locale: GroupLocale, raw: string, params: Record<string, string | number>): string {
  if (!raw.includes('|')) return raw
  return raw.replace(/\{(\w+)\|(\w+)\}/g, (match, k: string, noun: string) => {
    if (!(k in params)) return match
    const value = params[k]
    const n = typeof value === 'number' ? value : value !== undefined && String(value).trim() !== '' ? Number(value) : NaN
    return Number.isFinite(n) ? groupCount(locale, noun, n) : String(value)
  })
}

/**
 * R1 Arabic: a dual count is NOMINATIVE as `groupCount` writes it («غرفتان»),
 * but after a noun (iḍāfa), a preposition or as an object Arabic needs the
 * genitive / accusative «غرفتين» - "طلب الموافقة · مغادرة غرفتان" is wrong.
 * An Arabic template asks for that case with `{name:gen}` (the English template
 * keeps `{name}`); the value is turned to its oblique dual only when it IS one
 * of the known dual phrases of `units`, so a number or a name passes unchanged.
 */
function withArabicCase(raw: string, params: Record<string, string | number>): string {
  if (!raw.includes(':gen}')) return raw
  return raw.replace(/\{(\w+):gen\}/g, (match, k: string) =>
    k in params ? arabicOblique(String(params[k])) : match
  )
}

let OBLIQUE: Map<string, string> | null = null

/** A nominative Arabic dual count phrase → its genitive / accusative form; anything else unchanged. */
export function arabicOblique(phrase: string): string {
  if (!OBLIQUE) {
    OBLIQUE = new Map()
    const units = (lookup(ROOTS.ar, 'units') ?? {}) as Record<string, CountForms | undefined>
    for (const [noun, forms] of Object.entries(units)) {
      const two = forms?.two
      if (typeof two !== 'string' || noun.endsWith('_gen')) continue
      // The unit's own genitive set when it has one, else every «-ان» word as «-ين».
      const oblique = units[`${noun}_gen`]?.two ?? two.replace(/ان(?=\s|$)/g, 'ين')
      if (oblique !== two) OBLIQUE.set(two, oblique)
    }
  }
  return OBLIQUE.get(phrase.trim()) ?? phrase
}

/** True when `key` exists in BOTH locales — used by the vocabulary tests. */
export function hasGroupString(key: string): boolean {
  return typeof lookup(ROOTS.en, key) === 'string' && typeof lookup(ROOTS.ar, key) === 'string'
}

/** CLDR plural categories (Arabic uses all six). */
export type PluralCategory = 'zero' | 'one' | 'two' | 'few' | 'many' | 'other'

/** One template per category; `{n}` is replaced by the count. `other` is required. */
export type CountForms = Partial<Record<PluralCategory, string>> & { other: string }

const RULES: Record<GroupLocale, Intl.PluralRules> = {
  en: new Intl.PluralRules('en'),
  ar: new Intl.PluralRules('ar'),
}

/** The CLDR category of `n` in `locale` (ar: 0 zero · 1 one · 2 two · 3–10 few · 11–99 many · 100+ other). */
export function pluralCategory(locale: GroupLocale, n: number): PluralCategory {
  return RULES[locale].select(n) as PluralCategory
}

/** `n` with Latin digits and thousands separators ("1,250"), never Arabic-Indic. */
function latin(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

function fill(forms: CountForms, category: PluralCategory, n: number): string {
  return (forms[category] ?? forms.other).replace(/\{n\}/g, latin(n))
}

/**
 * An Arabic count with the correct plural form, via `Intl.PluralRules('ar')`:
 *   arCount(1, rooms) → «غرفة واحدة» · 2 → «غرفتان» · 3 → «3 غرف» · 15 → «15 غرفة» · 100 → «100 غرفة»
 * A missing category falls back to `other`. Digits stay Latin (brand rule).
 */
export function arCount(n: number, forms: CountForms): string {
  return fill(forms, pluralCategory('ar', Math.abs(n)), Math.abs(n))
}

/**
 * "{n} <noun>" in the right plural for the locale, from `dashboard.groupV2.units.<noun>`
 * (EN one/other; AR zero/one/two/few/many/other). Nouns: days, nights, rooms,
 * rooms_gen (after «من»), rooms_held, pax, guests, meals, buses, ids_gen,
 * ago_minutes, ago_hours, ago_days, critical, warnings.
 */
export function groupCount(locale: GroupLocale, noun: string, n: number): string {
  const node = (lookup(ROOTS[locale], `units.${noun}`) ?? lookup(ROOTS.en, `units.${noun}`)) as CountForms | undefined
  if (!node || typeof node.other !== 'string') return `${latin(n)} ${noun}`
  const abs = Math.abs(n)
  return fill(node, pluralCategory(locale, abs), abs)
}

/** "{n} days" — see {@link groupCount}. */
export function groupDays(locale: GroupLocale, n: number): string {
  return groupCount(locale, 'days', n)
}

/** "{n} nights" — see {@link groupCount}. */
export function groupNights(locale: GroupLocale, n: number): string {
  return groupCount(locale, 'nights', n)
}
